import React from 'react';

/**
 * Component Debug Utility
 * Provides comprehensive debugging information for React components
 * including dimensions, position, visibility, and rendering state
 */

export interface ComponentDebugInfo {
  componentName: string;
  elementId?: string;
  dimensions: {
    width: number;
    height: number;
    offsetWidth: number;
    offsetHeight: number;
    clientWidth: number;
    clientHeight: number;
    scrollWidth: number;
    scrollHeight: number;
  };
  position: {
    top: number;
    left: number;
    right: number;
    bottom: number;
    x: number;
    y: number;
  };
  visibility: {
    isVisible: boolean;
    isInViewport: boolean;
    opacity: number;
    display: string;
    visibility: string;
    overflow: string;
    zIndex: string;
  };
  styles: {
    computedStyles: Record<string, string>;
    backgroundColor: string;
    color: string;
    fontSize: string;
    padding: string;
    margin: string;
    border: string;
  };
  children: {
    childCount: number;
    hasContent: boolean;
    textContent: string;
  };
  renderState: {
    timestamp: number;
    isFirstRender: boolean;
    renderCount: number;
  };
  hierarchy: {
    parentElement?: string;
    nextSibling?: string;
    previousSibling?: string;
  };
}

class ComponentDebugger {
  private renderCounts = new Map<string, number>();
  private firstRenders = new Set<string>();
  private debugLogs: ComponentDebugInfo[] = [];
  private isEnabled = false;

  constructor() {
    // Enable in development or when explicitly requested - TEMPORARILY DISABLED
    this.isEnabled = false || window.location.search.includes('debug=true') || localStorage.getItem('component-debug') === 'true';

    if (this.isEnabled) {
      console.log('🔍 Component Debugger Enabled - Use window.ComponentDebugger to access');
      (window as any).ComponentDebugger = this;
    }
  }

  /**
   * Main debug function to be called from components
   */
  logComponent(
    componentName: string,
    elementRef: React.RefObject<HTMLElement> | HTMLElement | null,
    additionalInfo?: Record<string, any>,
  ): ComponentDebugInfo | null {
    if (!this.isEnabled) return null;

    const element = elementRef && 'current' in elementRef ? elementRef.current : elementRef;
    if (!element) {
      console.warn(`[ComponentDebugger] ${componentName}: No element ref provided`);
      return null;
    }

    const componentKey = `${componentName}-${element.tagName}`;
    const renderCount = (this.renderCounts.get(componentKey) || 0) + 1;
    this.renderCounts.set(componentKey, renderCount);

    const isFirstRender = !this.firstRenders.has(componentKey);
    if (isFirstRender) {
      this.firstRenders.add(componentKey);
    }

    const debugInfo = this.gatherDebugInfo(componentName, element, {
      isFirstRender,
      renderCount,
      additionalInfo,
    });

    this.debugLogs.push(debugInfo);

    // Log to console with color coding
    const color = this.getLogColor(debugInfo);
    console.group(`%c🔍 ${componentName} Debug Info`, `color: ${color}; font-weight: bold;`);
    console.log('📐 Dimensions:', debugInfo.dimensions);
    console.log('📍 Position:', debugInfo.position);
    console.log('👁️ Visibility:', debugInfo.visibility);
    console.log('🎨 Styles:', debugInfo.styles);
    console.log('👶 Children:', debugInfo.children);
    console.log('⚡ Render State:', debugInfo.renderState);
    if (additionalInfo) {
      console.log('➕ Additional Info:', additionalInfo);
    }
    console.groupEnd();

    return debugInfo;
  }

  private gatherDebugInfo(
    componentName: string,
    element: HTMLElement,
    context: { isFirstRender: boolean; renderCount: number; additionalInfo?: any },
  ): ComponentDebugInfo {
    const rect = element.getBoundingClientRect();
    const computedStyles = window.getComputedStyle(element);

    return {
      componentName,
      elementId: element.id || undefined,
      dimensions: {
        width: rect.width,
        height: rect.height,
        offsetWidth: element.offsetWidth,
        offsetHeight: element.offsetHeight,
        clientWidth: element.clientWidth,
        clientHeight: element.clientHeight,
        scrollWidth: element.scrollWidth,
        scrollHeight: element.scrollHeight,
      },
      position: {
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        x: rect.x,
        y: rect.y,
      },
      visibility: {
        isVisible: this.isElementVisible(element),
        isInViewport: this.isElementInViewport(element),
        opacity: parseFloat(computedStyles.opacity),
        display: computedStyles.display,
        visibility: computedStyles.visibility,
        overflow: computedStyles.overflow,
        zIndex: computedStyles.zIndex,
      },
      styles: {
        computedStyles: this.getRelevantStyles(computedStyles),
        backgroundColor: computedStyles.backgroundColor,
        color: computedStyles.color,
        fontSize: computedStyles.fontSize,
        padding: computedStyles.padding,
        margin: computedStyles.margin,
        border: computedStyles.border,
      },
      children: {
        childCount: element.children.length,
        hasContent: element.textContent ? element.textContent.trim().length > 0 : false,
        textContent: element.textContent ? element.textContent.slice(0, 100) : '',
      },
      renderState: {
        timestamp: Date.now(),
        isFirstRender: context.isFirstRender,
        renderCount: context.renderCount,
      },
      hierarchy: {
        parentElement: element.parentElement?.tagName || undefined,
        nextSibling: element.nextElementSibling?.tagName || undefined,
        previousSibling: element.previousElementSibling?.tagName || undefined,
      },
    };
  }

  private isElementVisible(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);

    return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  }

  private isElementInViewport(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  private getRelevantStyles(computedStyles: CSSStyleDeclaration): Record<string, string> {
    const relevantProps = [
      'position',
      'top',
      'left',
      'right',
      'bottom',
      'width',
      'height',
      'minWidth',
      'minHeight',
      'maxWidth',
      'maxHeight',
      'display',
      'flexDirection',
      'flexWrap',
      'justifyContent',
      'alignItems',
      'gridTemplateColumns',
      'gridTemplateRows',
      'gridArea',
      'transform',
      'transformOrigin',
    ];

    const styles: Record<string, string> = {};
    relevantProps.forEach((prop) => {
      styles[prop] = computedStyles.getPropertyValue(prop);
    });

    return styles;
  }

  private getLogColor(debugInfo: ComponentDebugInfo): string {
    // Color code based on visibility issues
    if (!debugInfo.visibility.isVisible) return '#ff4444'; // Red for invisible
    if (debugInfo.dimensions.width === 0 || debugInfo.dimensions.height === 0) return '#ff8800'; // Orange for zero size
    if (!debugInfo.visibility.isInViewport) return '#ffaa00'; // Yellow for out of viewport
    return '#00aa44'; // Green for good
  }

  // Public API methods
  enable(): void {
    this.isEnabled = true;
    localStorage.setItem('component-debug', 'true');
    console.log('🔍 Component Debugger Enabled');
  }

  disable(): void {
    this.isEnabled = false;
    localStorage.removeItem('component-debug');
    console.log('🔍 Component Debugger Disabled');
  }

  getLogs(): ComponentDebugInfo[] {
    return [...this.debugLogs];
  }

  getSummary() {
    const total = this.debugLogs.length;
    const invisible = this.getInvisibleComponents().length;
    const zeroSize = this.getZeroSizeComponents().length;

    return {
      totalComponents: total,
      activeComponents: total - invisible,
      invisibleComponents: invisible,
      zeroSizeComponents: zeroSize,
      avgRenderTime:
        this.debugLogs.length > 0 ? this.debugLogs.reduce((sum, log) => sum + ((log as any).timing?.renderTime || 0), 0) / this.debugLogs.length : 0,
      recentActivity: this.debugLogs.slice(-10).map((log) => ({
        timestamp: new Date().toISOString(),
        component: log.componentName,
        action: 'render',
      })),
    };
  }

  clearLogs(): void {
    this.debugLogs = [];
    this.renderCounts.clear();
    this.firstRenders.clear();
    console.log('🔍 Component Debug Logs Cleared');
  }

  getComponentsByName(name: string): ComponentDebugInfo[] {
    return this.debugLogs.filter((log) => log.componentName.toLowerCase().includes(name.toLowerCase()));
  }

  getInvisibleComponents(): ComponentDebugInfo[] {
    return this.debugLogs.filter((log) => !log.visibility.isVisible);
  }

  getZeroSizeComponents(): ComponentDebugInfo[] {
    return this.debugLogs.filter((log) => log.dimensions.width === 0 || log.dimensions.height === 0);
  }

  printSummary(): void {
    const total = this.debugLogs.length;
    const invisible = this.getInvisibleComponents().length;
    const zeroSize = this.getZeroSizeComponents().length;

    console.group('🔍 Component Debug Summary');
    console.log(`📊 Total Components Logged: ${total}`);
    console.log(`❌ Invisible Components: ${invisible}`);
    console.log(`📏 Zero-Size Components: ${zeroSize}`);
    console.log(`✅ Visible Components: ${total - invisible}`);
    console.groupEnd();
  }
}

// Create singleton instance
export const componentDebugger = new ComponentDebugger();

/**
 * Hook for debugging component render state
 */
export function useComponentDebug(
  componentName: string,
  elementRef: React.RefObject<HTMLElement>,
  additionalInfo?: Record<string, any>,
): ComponentDebugInfo | null {
  const [debugInfo, setDebugInfo] = React.useState<ComponentDebugInfo | null>(null);

  React.useEffect(() => {
    const info = componentDebugger.logComponent(componentName, elementRef, additionalInfo);
    setDebugInfo(info);
  }, [componentName, elementRef, additionalInfo]);

  return debugInfo;
}

/**
 * HOC for automatic component debugging
 */
export function withComponentDebug<P extends object>(WrappedComponent: React.ComponentType<P>, componentName?: string): React.ComponentType<P> {
  const ComponentWithDebug = React.forwardRef<HTMLElement, P>((props, ref) => {
    const elementRef = React.useRef<HTMLElement>(null);
    const name = componentName || WrappedComponent.displayName || WrappedComponent.name || 'Unknown';

    useComponentDebug(name, elementRef);

    return React.createElement(WrappedComponent, { ...props, ref: ref || elementRef } as any);
  });

  ComponentWithDebug.displayName = `withComponentDebug(${componentName || WrappedComponent.displayName || WrappedComponent.name})`;

  return ComponentWithDebug as unknown as React.ComponentType<P>;
}

// Global access
declare global {
  interface Window {
    ComponentDebugger: ComponentDebugger;
  }
}
